import {
  Button,
  Container,
  Divider,
  Grid,
  LinearProgress,
  Typography,
} from "@mui/material";
import { useTranslation } from "common/locales";
import { Form, Formik } from "formik";
import { FileEntry, FILE_UPLOAD_TYPE, useAppSelector } from "model";
import { useState } from "react";
import { useSelector } from "react-redux";

import { mixpanel } from "common/analytics/mixpanel";
import ConfirmCancellationDialog from "features/general/ConfirmCancellationDialog";
import GenericDropdown from "features/general/GenericDropdown";
import {
  AccessibleUser,
  selectAllAccessibleUsers,
} from "model/accessibleUsersSlice";
import * as Yup from "yup";
import ConfirmSubmitDialog from "./ConfirmSubmitDialog";
import { FileEntryFormData } from "./FileEntryFormData";
import { ButtonContainer, ButtonGroup } from "./generalMethods";
import PickFileButton from "./pickFileButton";

interface Props {
  fileEntry: FileEntry | undefined;
  handleSave: (fileEntry: FileEntryFormData) => Promise<void>;
  handleClose: () => void;
  title?: string;
}

const convertToFormInput = (fileEntry: FileEntry): FileEntryFormData => {
  return {
    fileDescription: fileEntry.fileDescription,
    fileName: fileEntry.fileName,
    fileUploadId: fileEntry.fileUploadId,
    associatedUserId: fileEntry.associatedUserId,
    fileUploadType: fileEntry.fileUploadType,
  };
};

export default function AddNewFileDialog({
  fileEntry,
  handleSave,
  handleClose,
  title,
}: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const appLoading = useAppSelector((state) => state.animalGroups.loading);

  const accessibleUsers: AccessibleUser[] = useSelector(
    selectAllAccessibleUsers
  );
  const user: AccessibleUser = useAppSelector(
    (state) => state.user.details
  ) as AccessibleUser;

  const getAccessibleUsersIds = () => {
    const intermediateAccessibleUsers: { [key: string]: string } = {};
    if (accessibleUsers) {
      accessibleUsers.forEach((accessibleUser) => {
        if (accessibleUser) {
          intermediateAccessibleUsers[accessibleUser.guid] =
            accessibleUser.name ?? accessibleUser.mail;
        }
      });
      if (user) {
        intermediateAccessibleUsers[user.guid] = user.name ?? user.mail;
      }
    }
    return intermediateAccessibleUsers;
  };

  const accessibleUserIdsAndNames = getAccessibleUsersIds();

  const [clicked, setClicked] = useState<boolean>(false);

  const FileUploadFormDataSchema = Yup.object({
    fileDescription: Yup.string().optional(),
    fileUploadType: Yup.mixed<FILE_UPLOAD_TYPE>()
      .oneOf(Object.values(FILE_UPLOAD_TYPE))
      .required(),
    file: Yup.string().optional(),
    fileName: Yup.string().required(),
    associatedUserId: Yup.string().required(),
    fileUploadId: Yup.string().optional(),
  });

  if (appLoading) {
    return <LinearProgress />;
  }

  const initialFileEntry = fileEntry ? convertToFormInput(fileEntry) : {};
  return (
    <>
      <Formik
        enableReinitialize={false}
        initialValues={initialFileEntry}
        validationSchema={FileUploadFormDataSchema}
        validateOnChange={false} // disable validation every field change, otherwise would validate all fields
        validateOnBlur={false} // disable validation every field blur, otherwise would validate all fields
        onSubmit={async (fileEntryFormData: FileEntryFormData) => {
          setClicked(true);
          await handleSave(fileEntryFormData);
          setClicked(false);
        }}
      >
        {({
          values,
          handleChange,
          errors,
          validateField,
          setFieldValue,
          submitForm,
        }) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const handleInputChange = async (e: any, fieldName: string) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            await (handleChange(e) as any); // async, typings are wrong
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            await (validateField(fieldName) as any); // validate only changed field, async, typings are wrong
          };

          return (
            <>
              <Form id="fileEntry-input-form">
                <Container style={{ textAlign: "left", marginBottom: 40 }}>
                  <Typography variant="h6">
                    {title || t("fileEntryInput.title")}
                  </Typography>
                </Container>

                <Container style={{ textAlign: "left", marginBottom: 40 }}>
                  <Grid
                    container
                    display="flex"
                    alignItems="top"
                    marginBottom={4}
                  >
                    <Grid item xs={12} display="flex" alignItems="top">
                      <GenericDropdown
                        disabled={false}
                        onChange={async (event) => {
                          await handleInputChange(event, `fileUploadType`);
                        }}
                        currentValue={values.fileUploadType}
                        inputError={errors?.fileUploadType ? true : false}
                        label={"fileEntries.fileUploadType.label"}
                        name={`fileUploadType`}
                        possibleValues={FILE_UPLOAD_TYPE}
                        tPrefix={"fileEntries.fileUploadType.label"}
                      />
                    </Grid>
                  </Grid>

                  <Grid
                    container
                    display="flex"
                    alignItems="top"
                    marginBottom={4}
                  >
                    <Grid item xs={12} display="flex" alignItems="top">
                      <GenericDropdown
                        disabled={false}
                        onChange={async (event) => {
                          await handleInputChange(event, `associatedUserId`);
                        }}
                        currentValue={values.associatedUserId}
                        inputError={errors?.associatedUserId ? true : false}
                        label={"fileEntries.associatedUserId.label"}
                        name={`associatedUserId`}
                        possibleValues={accessibleUserIdsAndNames}
                        tPrefix={""}
                        shouldTranslate={false}
                        flipKeyValue={true}
                      />
                    </Grid>
                  </Grid>

                  <Grid
                    item
                    xs={6}
                    sm={6}
                    display="flex"
                    alignItems="bottom"
                    marginBottom={4}
                  >
                    <PickFileButton setFieldValue={setFieldValue} />
                  </Grid>
                  <Grid item xs={6} sm={6} display="flex" alignItems="bottom">
                    <Typography>{values.fileName}</Typography>
                  </Grid>
                </Container>
              </Form>
              <ConfirmSubmitDialog
                confirmDialogOpen={confirmDialogOpen}
                closeDialog={() => setConfirmDialogOpen(false)}
                onConfirmSubmit={async () => {
                  await submitForm();
                  setConfirmDialogOpen(false);
                }}
                associatedFarmer={values.associatedUserId ?? ""}
                onConfirmCancel={() => setConfirmDialogOpen(false)}
                submitDisabled={clicked}
              />
              <Divider />
              <ButtonContainer>
                <ButtonGroup>
                  <Button
                    color="secondary"
                    onClick={handleCancel}
                    disabled={clicked}
                  >
                    {t("edit.button.cancel")}
                  </Button>
                  <Button
                    color="primary"
                    onClick={() => setConfirmDialogOpen(true)}
                    disabled={
                      clicked ||
                      !values.fileName ||
                      !values.associatedUserId ||
                      !values.fileUploadType
                    }
                  >
                    {t("edit.button.save")}
                  </Button>
                </ButtonGroup>
                <ConfirmCancellationDialog
                  dialogOpen={open}
                  closeDialog={() => setOpen(false)}
                  onConfirmCancel={() => handleConfirmationClose()}
                  onCancelCancel={() => setOpen(false)}
                />
              </ButtonContainer>
            </>
          );
        }}
      </Formik>
    </>
  );

  function handleConfirmationClose() {
    mixpanel.track("FieldPreparationEvent creation cancelled");
    handleClose();
  }

  function handleCancel() {
    setOpen(true);
  }
}
